import { NoResults } from "../../../components/NoResults/NoResults.js";
import { addDays, startOfDay } from "../../../components/Calendar/utils/date-grid.js";
import { Calendar } from "../../../components/Calendar/Calendar.js";
import { ErrorLoadingRetry } from "../../../components/ErrorLoadingRetry.js";
import { ServicePeriodInstanceCard } from "../instance/ServicePeriodInstanceCard.js";
import { dayPerformanceClassName, rangePerformanceCounts } from "./calendar-event-from-instance.js";
import { ServicePeriodCalendarDayCell } from "./ServicePeriodCalendarDayCell.js";
import { ServicePeriodCalendarLegend } from "./ServicePeriodCalendarLegend.js";
import { ServicePeriodCalendarSummary } from "./ServicePeriodCalendarSummary.js";
import dayjs from "dayjs";
import { useState } from "react";
import { Loading, Modal } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { RiCalendarScheduleFill } from "react-icons/ri";
//#region src/modules/ServicePeriods/calendar/ServicePeriodInstancesCalendarView.tsx
dayjs.extend(utc);
dayjs.extend(timezone);
function formatDayLabel(dayStartMs, timeZone) {
	return (timeZone ? dayjs(dayStartMs).tz(timeZone) : dayjs(dayStartMs)).format("dddd, MMMM D, YYYY");
}
function formatStartTime(ms, timeZone) {
	return (timeZone ? dayjs(ms).tz(timeZone) : dayjs(ms)).format("h:mm A");
}
function eventsOnDay(events, dayStartMs, timeZone) {
	const dayEndMs = addDays(dayStartMs, 1, timeZone);
	return events.filter((event) => event.startsAt < dayEndMs && event.endsAt > dayStartMs);
}
function ServicePeriodInstancesCalendarView({ anchorMs, error, evalsBySourceRefId, events, isLoading, onAnchorChange, onRetry, onViewChange, renderInstance, timeZone, view }) {
	const [selected, setSelected] = useState();
	const counts = rangePerformanceCounts(events);
	const renderCard = renderInstance ?? ((args) => /* @__PURE__ */ jsx(ServicePeriodInstanceCard, {
		instance: args.instance,
		runtimeActivityEvals: args.runtimeActivityEvals
	}));
	const selectedEvents = selected?.type === "day" ? eventsOnDay(events, selected.dayStartMs, timeZone) : selected?.type === "event" ? [selected.event] : [];
	const selectedHeader = selected?.type === "day" ? formatDayLabel(selected.dayStartMs, timeZone) : selected?.type === "event" ? `${selected.event.data.instance.name} · ${formatDayLabel(startOfDay(selected.event.startsAt, timeZone), timeZone)}` : void 0;
	return /* @__PURE__ */ jsxs("div", {
		className: "relative",
		children: [
			/* @__PURE__ */ jsx(Loading, {
				overlay: true,
				size: "xxl",
				visible: isLoading && !events.length
			}),
			error && /* @__PURE__ */ jsx("div", {
				className: "mb-xs",
				children: /* @__PURE__ */ jsx(ErrorLoadingRetry, { onRetry })
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "gap-sm flex flex-col",
				children: [
					/* @__PURE__ */ jsxs("div", {
						className: "gap-sm flex flex-col sm:flex-row sm:items-center sm:justify-between",
						children: [/* @__PURE__ */ jsx(ServicePeriodCalendarSummary, {
							fulfilled: counts.fulfilled,
							missed: counts.missed,
							partial: counts.partial,
							total: counts.total
						}), /* @__PURE__ */ jsx(ServicePeriodCalendarLegend, {})]
					}),
					/* @__PURE__ */ jsx(Calendar, {
						anchorMs,
						dayClassName: (_day, segments) => dayPerformanceClassName(segments.map((seg) => seg.event)),
						events,
						onAnchorChange,
						onDayClick: (dayStartMs) => {
							if (!eventsOnDay(events, dayStartMs, timeZone).length) return;
							setSelected({
								dayStartMs,
								type: "day"
							});
						},
						onEventClick: (event) => setSelected({
							event,
							type: "event"
						}),
						onViewChange,
						renderDayContent: (_day, segments) => /* @__PURE__ */ jsx(ServicePeriodCalendarDayCell, { segments }),
						renderEventContent: (event, segment) => /* @__PURE__ */ jsx("span", {
							className: "px-xxs",
							children: segment.clippedStart ? event.data.instance.name : `${formatStartTime(event.startsAt, timeZone)} ${event.data.instance.name}`
						}),
						timeZone,
						view
					}),
					!isLoading && !error && !events.length && /* @__PURE__ */ jsx(NoResults, {
						iconName: RiCalendarScheduleFill,
						text: "No service periods in this range"
					})
				]
			}),
			selected && /* @__PURE__ */ jsx(Modal, {
				closeHandler: () => setSelected(void 0),
				escPressClose: true,
				header: selectedHeader,
				width: "lg",
				children: /* @__PURE__ */ jsx("div", {
					className: "gap-sm flex flex-col",
					children: selectedEvents.map((event) => /* @__PURE__ */ jsx("div", { children: renderCard({
						instance: event.data.instance,
						runtimeActivityEvals: evalsBySourceRefId[event.data.instance.id]
					}) }, event.key))
				})
			})
		]
	});
}
//#endregion
export { ServicePeriodInstancesCalendarView };

//# sourceMappingURL=ServicePeriodInstancesCalendarView.js.map